import React, { useState, useEffect } from 'react';

/**
 * PlanRenewalModal
 * Shows when a user wants to renew or extend their subscription plan, or switch cycles.
 *
 * Props:
 *   isOpen           - boolean
 *   onClose          - fn()
 *   companyProfile   - company profile object (has subscription details)
 *   token            - auth token
 *   onSuccess        - fn() called after successful renewal/upgrade (refresh parent)
 */
const PlanRenewalModal = ({ isOpen, onClose, companyProfile, token, onSuccess, buyExtraBranch = false }) => {
    const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:5000'
        : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

    // Monthly prices
    const PLAN_MONTHLY_PRICES = {
        starter: 399,
        premium: 799
    };

    // Calculate plan price based on duration
    const getPlanPrice = (plan, period) => {
        const monthly = PLAN_MONTHLY_PRICES[plan] || 399;
        if (period === 'halfYearly') return monthly * 6;
        return monthly * 12;
    };

    const isPaidUser = Boolean(companyProfile?.has_paid && companyProfile?.has_paid !== 0 && companyProfile?.has_paid !== '0');
    const rawPlan = (companyProfile?.subscription_plan || 'starter').toLowerCase();
    const isTrialUser = !isPaidUser || rawPlan === 'trial';
    const currentPlan = isTrialUser ? 'trial' : rawPlan;
    
    // State
    const [selectedPlan, setSelectedPlan] = useState(isTrialUser ? 'starter' : (rawPlan === 'premium' ? 'premium' : 'starter'));
    const [billingPeriod, setBillingPeriod] = useState('yearly');
    const [currentBillingPeriod, setCurrentBillingPeriod] = useState('yearly');
    
    /* eslint-disable no-unused-vars */
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState('');
    /* eslint-enable no-unused-vars */
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState('');

    const [branchCount, setBranchCount] = useState(1);
    const [existingBranchCount, setExistingBranchCount] = useState(1);

    const normalizePeriod = (period) => {
        if (!period) return 'yearly';
        const p = period.toLowerCase();
        if (p.includes('half') || p.includes('6') || p.includes('six')) {
            return 'halfYearly';
        }
        return 'yearly';
    };

    // Setup initial states from profile directly on open
    useEffect(() => {
        if (isOpen) {
            const plan = (companyProfile?.subscription_plan || 'starter').toLowerCase();
            setSelectedPlan(plan);
            const activePeriod = normalizePeriod(companyProfile?.billing_period);
            setCurrentBillingPeriod(activePeriod);
            setBillingPeriod(activePeriod);
            setQuote(null);
            setQuoteError('');
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch branches to set existing count and current limit
    useEffect(() => {
        if (isOpen && token) {
            const fetchBranches = async () => {
                try {
                    const res = await fetch(`${API_URL}/api/branches`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if (data.success && Array.isArray(data.data)) {
                        const activeBranches = data.data.filter(b => b.is_active && (b.is_active === true || b.is_active === 1 || b.is_active === '1' || b.is_active === 'true'));
                        const count = activeBranches.length;
                        setExistingBranchCount(count);
                        const currentLimit = companyProfile?.branch_limit || 1;
                        let defaultCount = Math.max(count, currentLimit);
                        if (buyExtraBranch) {
                            defaultCount += 1;
                        }
                        setBranchCount(defaultCount);
                    }
                } catch (err) {
                    console.error('Failed to fetch branches:', err);
                }
            };
            fetchBranches();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Calculate dates
    const now = new Date();
    const paidEndDate = (isPaidUser && companyProfile?.subscription_end_date) ? new Date(companyProfile.subscription_end_date) : null;
    const isPaidActive = Boolean(isPaidUser && paidEndDate && paidEndDate > now);

    const rawEndDate = companyProfile?.subscription_end_date || companyProfile?.trial_ends_at || companyProfile?.trial_end_date;
    const currentEndDate = rawEndDate ? new Date(rawEndDate) : null;
    const isExpired = currentEndDate ? currentEndDate < now : true;
    const daysRemaining = (currentEndDate && !isExpired) ? Math.max(0, Math.round((currentEndDate - now) / (1000 * 60 * 60 * 24))) : 0;

    // Default duration selector to 'current_cycle' for tier upgrades
    useEffect(() => {
        if (isOpen) {
            const isTierUpgrade = isPaidUser && currentPlan === 'starter' && selectedPlan === 'premium';
            if (isTierUpgrade) {
                setBillingPeriod('current_cycle');
            } else {
                setBillingPeriod(currentBillingPeriod);
            }
        }
    }, [selectedPlan, isOpen, currentPlan, currentBillingPeriod, isPaidUser]);

    // Check relationship between current plan and selected plan
    const isSamePlan = isPaidUser && selectedPlan === currentPlan;
    
    // Upgrade: ONLY for active paid users mid-cycle
    const isBranchUpgrade = isPaidUser && selectedPlan === currentPlan && branchCount > (companyProfile?.branch_limit || 1);
    const isUpgrade = isPaidUser && (!isExpired) && ((currentPlan === 'starter' && selectedPlan === 'premium') || isBranchUpgrade);

    // Degradation:
    // 1. Premium -> Starter
    // 2. Same plan, but Yearly -> Half-Yearly
    const isTierDegradation = isPaidUser && currentPlan === 'premium' && selectedPlan === 'starter';
    const isCycleDegradation = isSamePlan && currentBillingPeriod === 'yearly' && billingPeriod === 'halfYearly';
    const isDegradation = isTierDegradation || isCycleDegradation;

    // Renewal: any transaction that is NOT a prorated upgrade
    const isRenewal = !isUpgrade;

    // Renewal starting point: if active paid user, append to paid end date; if trial or expired, start TODAY
    const baseStartDate = isPaidActive ? paidEndDate : now;

    // New expiration date calculation
    let calculatedNewEndDate = new Date(baseStartDate);
    if (isUpgrade) {
        if (currentEndDate && !isExpired) {
            if (billingPeriod === 'current_cycle') {
                calculatedNewEndDate = new Date(currentEndDate);
            } else if (billingPeriod === 'halfYearly') {
                calculatedNewEndDate = new Date(currentEndDate);
                calculatedNewEndDate.setMonth(calculatedNewEndDate.getMonth() + 6);
            } else {
                calculatedNewEndDate = new Date(currentEndDate);
                calculatedNewEndDate.setFullYear(calculatedNewEndDate.getFullYear() + 1);
            }
        } else {
            if (billingPeriod === 'halfYearly') {
                calculatedNewEndDate.setMonth(calculatedNewEndDate.getMonth() + 6);
            } else {
                calculatedNewEndDate.setFullYear(calculatedNewEndDate.getFullYear() + 1);
            }
        }
    } else {
        if (billingPeriod === 'halfYearly') {
            calculatedNewEndDate.setMonth(calculatedNewEndDate.getMonth() + 6);
        } else {
            calculatedNewEndDate.setFullYear(calculatedNewEndDate.getFullYear() + 1);
        }
    }

    // If it is a branch-upgrade only (same plan tier, extra branch), the end date remains same as current cycle end date
    if (isUpgrade && currentPlan === selectedPlan) {
        calculatedNewEndDate = currentEndDate ? new Date(currentEndDate) : now;
    }

    // Fallback: If we have a backend quote response with the exact date, use it
    if (isUpgrade && quote && quote.new_subscription_end_date) {
        calculatedNewEndDate = new Date(quote.new_subscription_end_date);
    }

    const newEndDateReadable = calculatedNewEndDate.toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric'
    });

    const currentEndDateReadable = currentEndDate 
        ? currentEndDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
        : 'N/A';

    // Amount to pay (Priced per branch for all plans)
    let amountToPay = getPlanPrice(selectedPlan, billingPeriod) * branchCount;
    if (isUpgrade && quote && quote.prorated_amount !== undefined && quote.prorated_amount !== null && !isNaN(quote.prorated_amount)) {
        amountToPay = quote.prorated_amount;
    }

    // Handle checkout payment
    const handleProceedToPayment = async () => {
        setPaymentLoading(true);
        setPaymentError('');

        try {
            const body = {
                plan_type: selectedPlan,
                amount: amountToPay,
                currency: 'INR',
                billing_period: billingPeriod,
                branch_limit: branchCount,
                is_upgrade: isUpgrade,
                upgraded_from: isUpgrade ? currentPlan : null,
                is_renewal: isRenewal
            };

            const orderRes = await fetch(`${API_URL}/api/payment/create-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            const orderData = await orderRes.json();
            if (!orderData.success) throw new Error(orderData.message || 'Failed to create order');

            if (orderData.gateway === 'razorpay') {
                const options = {
                    key: orderData.razorpay_key,
                    amount: orderData.amount,
                    currency: orderData.currency,
                    name: 'RedSorm',
                    description: isUpgrade 
                        ? `Upgrade Plan to ${selectedPlan.toUpperCase()}`
                        : `Renew ${selectedPlan.toUpperCase()} Plan (${billingPeriod === 'halfYearly' ? '6 Months' : '1 Year'})`,
                    order_id: orderData.razorpay_order_id,
                    handler: async (response) => {
                        setPaymentLoading(true);
                        try {
                            const verifyRes = await fetch(`${API_URL}/api/payment/verify`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                    order_id: orderData.order_id,
                                    razorpay_payment_id: response.razorpay_payment_id,
                                    razorpay_order_id: response.razorpay_order_id,
                                    razorpay_signature: response.razorpay_signature
                                })
                            });
                            const verifyData = await verifyRes.json();
                            if (verifyData.success) {
                                onClose();
                                if (onSuccess) onSuccess();
                                window.location.href = `/payment/status?order_id=${orderData.order_id}`;
                            } else {
                                throw new Error(verifyData.message || 'Payment verification failed');
                            }
                        } catch (vErr) {
                            setPaymentError(vErr.message);
                            setPaymentLoading(false);
                        }
                    },
                    prefill: {
                        name: orderData.customer_name,
                        email: orderData.customer_email,
                        contact: orderData.customer_phone
                    },
                    theme: { color: '#6366f1' },
                    modal: {
                        ondismiss: () => setPaymentLoading(false)
                    }
                };
                const rzp = new window.Razorpay(options);
                rzp.open();
            } else {
                // Cashfree Fallback
                const { load } = await import('@cashfreepayments/cashfree-js');
                const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
                const cashfree = await load({ mode: isProduction ? 'production' : 'sandbox' });
                cashfree.checkout({ paymentSessionId: orderData.payment_session_id, redirectTarget: '_self' });
            }
        } catch (err) {
            setPaymentError(err.message || 'Something went wrong. Please try again.');
            setPaymentLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm" onClick={onClose} />

            {/* Modal Box */}
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up max-h-[90vh] flex flex-col">
                
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                            <i className="fas fa-sync-alt text-white text-lg" />
                        </div>
                        <div>
                            <h2 className="text-white font-bold text-lg leading-tight">Renew / Extend Plan</h2>
                            <p className="text-white/70 text-xs">Keep your business running smoothly</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition text-white">
                        <i className="fas fa-times" />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-5 overflow-y-auto flex-1">
                    
                    {/* Current Plan Overview Card */}
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex justify-between items-center">
                        <div>
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Current Subscription</p>
                            <p className="font-extrabold text-gray-800 text-base mt-0.5">
                                {isTrialUser ? 'FREE TRIAL' : `${currentPlan.toUpperCase()} Plan (${currentBillingPeriod === 'halfYearly' ? '6 Months' : '1 Year'})`}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                                {isTrialUser ? (isExpired ? 'Trial Expired On' : 'Trial Expires On') : (isExpired ? 'Expired On' : 'Expires On')}
                            </p>
                            <p className={`font-bold text-sm mt-0.5 ${isExpired ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {currentEndDateReadable}
                            </p>
                        </div>
                    </div>

                    {/* Plan Selector */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Select Plan Tier</label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setSelectedPlan('starter')}
                                className={`p-4 rounded-2xl border-2 transition text-left relative ${selectedPlan === 'starter'
                                    ? 'border-indigo-500 bg-indigo-50/50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                            >
                                <p className="font-bold text-sm text-gray-800">STARTER</p>
                                <p className="text-xs text-gray-500 mt-1">₹{getPlanPrice('starter', billingPeriod) * branchCount} / {billingPeriod === 'halfYearly' ? '6 Months' : '1 Year'}</p>
                                {currentPlan === 'starter' && (
                                    <span className="absolute top-2 right-2 bg-indigo-100 text-indigo-700 text-[9px] px-1.5 py-0.5 rounded font-black">CURRENT</span>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setSelectedPlan('premium')}
                                className={`p-4 rounded-2xl border-2 transition text-left relative ${selectedPlan === 'premium'
                                    ? 'border-purple-500 bg-purple-50/50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                            >
                                <p className="font-bold text-sm text-gray-800">PREMIUM</p>
                                <p className="text-xs text-gray-500 mt-1">₹{getPlanPrice('premium', billingPeriod) * branchCount} / {billingPeriod === 'halfYearly' ? '6 Months' : '1 Year'}</p>
                                {currentPlan === 'premium' && (
                                    <span className="absolute top-2 right-2 bg-purple-100 text-purple-700 text-[9px] px-1.5 py-0.5 rounded font-black">CURRENT</span>
                                )}
                            </button>
                        </div>
                    </div>
                    {/* Branch Selector (Allows choosing count on upgrade, or keeping/decreasing on renewal) */}
                    {(selectedPlan === 'premium' || selectedPlan === 'starter') && (
                        <div className="bg-purple-50/50 border border-purple-200 rounded-3xl p-5 space-y-3">
                            <label className="block text-sm font-black text-purple-950">Number of Branches / Outlets</label>
                            <div className="flex items-center gap-4">
                                <button
                                    type="button"
                                    onClick={() => setBranchCount(prev => Math.max(1, prev - 1))}
                                    disabled={branchCount <= 1}
                                    className="w-10 h-10 bg-white border-2 border-purple-200 rounded-xl flex items-center justify-center text-purple-700 font-bold hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition active:scale-95"
                                >
                                    <i className="fas fa-minus"></i>
                                </button>
                                <span className="text-lg font-black text-purple-900 w-12 text-center">{branchCount}</span>
                                <button
                                    type="button"
                                    onClick={() => setBranchCount(prev => prev + 1)}
                                    disabled={branchCount >= 100}
                                    className="w-10 h-10 bg-white border-2 border-purple-200 rounded-xl flex items-center justify-center text-purple-700 font-bold hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition active:scale-95"
                                >
                                    <i className="fas fa-plus"></i>
                                </button>
                                <p className="text-xs text-purple-700 leading-tight">
                                    {selectedPlan === currentPlan 
                                        ? `You can adjust your licensed outlets. Minimum is 1. (Active count: ${existingBranchCount || 1})`
                                        : `Choose the number of branches/outlets to purchase with your new plan. (Active count: ${existingBranchCount || 1})`}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Billing Period Selector */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Select Duration</label>
                        <div className={`grid ${isUpgrade ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                            {isUpgrade && (
                                <button
                                    type="button"
                                    onClick={() => setBillingPeriod('current_cycle')}
                                    className={`p-3 rounded-2xl border-2 transition text-left relative ${billingPeriod === 'current_cycle'
                                        ? 'border-indigo-500 bg-indigo-50/50'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                >
                                    <p className="font-bold text-sm text-gray-800">Remaining</p>
                                    <p className="text-[10px] text-gray-500 mt-1">{daysRemaining} active days</p>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setBillingPeriod('halfYearly')}
                                className={`p-3 rounded-2xl border-2 transition text-left relative ${billingPeriod === 'halfYearly'
                                    ? 'border-indigo-500 bg-indigo-50/50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                            >
                                <p className="font-bold text-sm text-gray-800">{isUpgrade ? '6 Mo. Ext.' : '6 Months'}</p>
                                <p className="text-[10px] text-gray-500 mt-1">{isUpgrade ? '+6 months added' : 'Half-Yearly billing'}</p>
                                {!isUpgrade && currentBillingPeriod === 'halfYearly' && (
                                    <span className="absolute top-2 right-2 bg-indigo-100 text-indigo-700 text-[9px] px-1.5 py-0.5 rounded font-black">CURRENT</span>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setBillingPeriod('yearly')}
                                className={`p-3 rounded-2xl border-2 transition text-left relative ${billingPeriod === 'yearly'
                                    ? 'border-purple-500 bg-purple-50/50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                            >
                                <p className="font-bold text-sm text-gray-800">{isUpgrade ? '12 Mo. Ext.' : '12 Months'}</p>
                                <p className="text-[10px] text-gray-500 mt-1">{isUpgrade ? '+12 months added' : 'Yearly billing'}</p>
                                {!isUpgrade && currentBillingPeriod === 'yearly' && (
                                    <span className="absolute top-2 right-2 bg-purple-100 text-purple-700 text-[9px] px-1.5 py-0.5 rounded font-black">CURRENT</span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Upgrade calculations / loader */}
                    {isUpgrade && (
                        <>
                            {quoteLoading && (
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex items-center justify-center gap-2 text-gray-400">
                                    <i className="fas fa-spinner fa-spin" />
                                    <span className="text-sm">Calculating prorated amount...</span>
                                </div>
                            )}

                            {quoteError && !quoteLoading && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600 flex items-start gap-2">
                                    <i className="fas fa-exclamation-circle mt-0.5 flex-shrink-0" />
                                    <span>{quoteError}</span>
                                </div>
                            )}

                            {quote && !quoteLoading && (
                                <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-xl overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-violet-200 flex items-center justify-between text-xs text-violet-700 font-bold bg-violet-100/50">
                                        <span className="flex items-center gap-1.5">
                                            <i className="fas fa-calculator" /> Proration Applied
                                        </span>
                                        <span>{quote.days_remaining} active days left</span>
                                    </div>
                                    <div className="px-4 py-3 space-y-2 text-xs text-gray-600">
                                        <div className="flex justify-between">
                                            <span>Unused credit (from current plan)</span>
                                            <span className="font-bold text-emerald-600">− ₹{quote.unused_credit}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>New plan cost ({selectedPlan.toUpperCase()})</span>
                                            <span className="font-bold text-indigo-700">₹{quote.new_plan_prorated_cost}</span>
                                        </div>
                                        <div className="border-t border-violet-200 pt-2 flex justify-between items-center text-sm font-black text-gray-800">
                                            <span>Upgrade Price Today</span>
                                            <span className="text-base text-violet-700">₹{amountToPay}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Degradation Warning */}
                    {isDegradation && (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-1 text-xs text-amber-800">
                            <div className="flex items-center gap-2 font-bold text-amber-950">
                                <i className="fas fa-exclamation-triangle text-amber-600" />
                                <span>Plan Degradation Notice</span>
                            </div>
                            <p className="leading-relaxed">
                                You are choosing to downgrade your plan/billing cycle. Since no refunds are issued for the active subscription cycle, you will keep your full <strong>{currentPlan.toUpperCase()}</strong> benefits until your current expiration date (<strong>{currentEndDateReadable}</strong>). Your new plan will take effect automatically after that date.
                            </p>
                        </div>
                    )}

                    {/* New Subscription Expiration Preview Card */}
                    <div className="bg-indigo-50/50 border border-indigo-100 rounded-2xl p-4 space-y-2">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500 font-medium">New Subscription End Date</span>
                            <span className="bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full text-[10px]">
                                {isDegradation 
                                    ? 'Scheduled Transition' 
                                    : isUpgrade 
                                        ? billingPeriod === 'current_cycle' ? 'Upgrade Remaining' : 'Upgrade & Extend'
                                        : isPaidActive ? 'Duration Appended' : 'Starts Today'}
                            </span>
                        </div>
                        <div className="text-gray-800 font-extrabold text-base flex items-center gap-2">
                            <i className="far fa-calendar-alt text-indigo-600" />
                            <span>{newEndDateReadable}</span>
                        </div>
                        <p className="text-[10px] text-gray-400">
                            {isDegradation
                                ? `You will retain your current ${currentPlan.toUpperCase()} benefits until ${currentEndDateReadable}. The new ${selectedPlan.toUpperCase()} plan will start immediately after.`
                                : isUpgrade
                                    ? billingPeriod === 'current_cycle'
                                        ? `We have upgraded the remaining ${daysRemaining} days of your current cycle to Premium. A credit of ₹${quote ? quote.unused_credit : 0} from your Starter plan has been applied to reduce the price today.`
                                        : `We have upgraded your remaining ${daysRemaining} days to Premium and extended your subscription by ${billingPeriod === 'halfYearly' ? '6 Months' : '1 Year'}. A credit of ₹${quote ? quote.unused_credit : 0} from your Starter plan has been applied to reduce the price.`
                                    : isTrialUser
                                        ? `Your new subscription starts today.`
                                        : isExpired
                                            ? "Your plan starts today since your previous plan has expired."
                                            : `We've appended ${billingPeriod === 'halfYearly' ? '6 Months' : '1 Year'} directly onto your remaining days. No paid days lost.`
                            }
                        </p>
                    </div>

                    {/* Errors */}
                    {paymentError && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600 flex items-start gap-2">
                            <i className="fas fa-exclamation-triangle mt-0.5 flex-shrink-0" />
                            <span>{paymentError}</span>
                        </div>
                    )}

                    {/* Payment CTA Buttons */}
                    <div className="flex flex-col gap-2 pt-1">
                        <button
                            type="button"
                            onClick={handleProceedToPayment}
                            disabled={paymentLoading}
                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3.5 rounded-2xl transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {paymentLoading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-shield-alt" />
                                    {isUpgrade ? 'Proceed to Upgrade' : 'Renew Subscription'} · ₹{amountToPay}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-2xl transition-all text-sm active:scale-95"
                        >
                            Cancel
                        </button>
                    </div>

                    <p className="text-center text-gray-400 text-[10px]">
                        <i className="fas fa-lock mr-1" /> Secure payments via Razorpay & Cashfree.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PlanRenewalModal;
